import { useEffect, useMemo, useState } from "react";
import { ApiError, buildIntakeApiClient } from "../api/client.js";
import { DocumentUploadSlots, MAX_BYTES } from "../components/DocumentUploadSlots.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { uploadDirect } from "../lib/upload-direct.js";
import { formatLocal } from "../lib/format.js";
import { postDocsEvent, startResizeObserver } from "../lib/post-message.js";
import type { DocumentKind, DocumentsSession, PaymentMethod } from "../api/types.js";

const KINDS_BY_METHOD: Record<PaymentMethod, readonly DocumentKind[]> = {
  insurance: ["drivers_license_front", "drivers_license_back", "insurance_front", "insurance_back"],
  self_pay: ["drivers_license_front", "drivers_license_back"],
};

const PAYMENT_OPTIONS: ReadonlyArray<{ value: PaymentMethod; label: string }> = [
  { value: "insurance", label: "Insurance" },
  { value: "self_pay", label: "Self-pay" },
];

const INVALID_LINK =
  "This link isn't valid anymore. You can bring your ID and insurance card to your visit.";

type State =
  | { status: "loading" }
  | { status: "invalid" }
  | { status: "error"; message: string }
  | { status: "ready"; session: DocumentsSession };

const isNotFound = (err: unknown) => err instanceof ApiError && err.status === 404;

// Standalone bookings redirect here with ?booked=<confirmationId> so the
// patient still sees their confirmation.
function bookedConfirmation(): string | null {
  if (typeof window === "undefined") return null;
  const code = new URLSearchParams(window.location.search).get("booked");
  return code ? code : null;
}

export function DocumentsPage({ token }: { token: string }) {
  const api = useMemo(() => buildIntakeApiClient(), []);
  const [state, setState] = useState<State>({ status: "loading" });
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [uploaded, setUploaded] = useState<DocumentKind[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [booked] = useState(bookedConfirmation);
  const [bringLater, setBringLater] = useState(false);

  useEffect(() => startResizeObserver(), []);

  useEffect(() => {
    let cancelled = false;
    api.getDocumentsSession(token).then(
      (session) => {
        if (cancelled) return;
        setMethod(session.paymentMethod);
        setUploaded(session.uploaded);
        setState({ status: "ready", session });
      },
      (err: unknown) => {
        if (cancelled) return;
        setState(isNotFound(err)
          ? { status: "invalid" }
          : { status: "error", message: "We couldn't load this page. Please try again." });
      },
    );
    return () => { cancelled = true; };
  }, [api, token]);

  const handleError = (err: unknown) => {
    if (isNotFound(err)) setState({ status: "invalid" });
  };

  async function choose(next: PaymentMethod) {
    setActionError(null);
    try {
      await api.setPaymentMethod(token, next);
    } catch (err) {
      if (isNotFound(err)) setState({ status: "invalid" });
      else setActionError("We couldn't save that. Please try again.");
      return;
    }
    setMethod(next);
    setChoosing(false);
    postDocsEvent({ name: "docs_payment_selected", method: next });
  }

  async function upload(kind: DocumentKind, file: File) {
    const { document_id, sas_url } = await api.issueDocumentUploadUrl(token, kind, file);
    await uploadDirect(sas_url, file);
    await api.commitDocument(token, document_id, kind);
  }

  function handleUploaded(kind: DocumentKind) {
    setUploaded((prev) => (prev.includes(kind) ? prev : [...prev, kind]));
    // This page only posts analytics when embedded in the website's iframe,
    // i.e. right after booking.
    postDocsEvent({ name: "docs_uploaded", kind, when: "now" });
  }

  if (state.status === "loading") {
    return (
      <main className="mx-auto max-w-md p-6 text-center">
        <p className="text-sm text-gray-700">Loading…</p>
      </main>
    );
  }
  if (state.status === "invalid") {
    return (
      <main className="mx-auto max-w-md p-6 text-center">
        <p className="text-sm text-gray-700">{INVALID_LINK}</p>
      </main>
    );
  }
  if (state.status === "error") {
    return (
      <main className="mx-auto max-w-md p-6">
        <ErrorBanner message={state.message} />
      </main>
    );
  }

  const { session } = state;
  const kinds = method && !choosing ? KINDS_BY_METHOD[method] : null;
  const allDone = kinds !== null && kinds.every((k) => uploaded.includes(k));
  const title = method === "self_pay" && !choosing ? "Add your ID" : "Add your ID and insurance";
  const where = session.locationName ? ` at ${session.locationName}` : "";

  return (
    <main className="mx-auto max-w-md p-6">
      {booked ? (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-slate-900">You're booked</h2>
          <p className="mt-1 text-sm text-slate-600">
            {`Confirmation ${booked} — we've emailed you the details.`}
          </p>
        </div>
      ) : null}
      <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
      <p className="mt-1 text-sm text-slate-600">
        {`Hi ${session.firstName}, your ${session.visitTypeName} is on ${formatLocal(session.startsAt)}${where}`}
      </p>

      {actionError ? <div className="mt-4"><ErrorBanner message={actionError} /></div> : null}

      {bringLater ? (
        <p className="mt-6 text-sm font-medium text-emerald-700">All set — see you at your visit.</p>
      ) : kinds === null ? (
        <fieldset className="mt-6 grid gap-3">
          <legend className="text-sm font-medium text-slate-700">
            How will you pay for this visit?
          </legend>
          <div className="grid grid-cols-2 gap-3">
            {PAYMENT_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => void choose(value)}
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-blue-400 hover:bg-blue-50"
              >
                {label}
              </button>
            ))}
          </div>
        </fieldset>
      ) : (
        <section className="mt-6 grid gap-3">
          <p className="text-xs text-slate-500">
            {method === "self_pay" ? "Self-pay" : "Insurance"} ·{" "}
            <button
              type="button"
              onClick={() => setChoosing(true)}
              className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              Change
            </button>
          </p>
          <p className="text-xs text-slate-500">
            Take a clear photo of each side. JPG, PNG, or PDF up to{" "}
            {MAX_BYTES / (1024 * 1024)} MB.
          </p>
          <DocumentUploadSlots
            kinds={kinds}
            uploaded={uploaded}
            upload={upload}
            onUploaded={handleUploaded}
            onError={handleError}
          />
          {allDone ? (
            <p className="text-sm font-medium text-emerald-700">All set — see you at your visit.</p>
          ) : null}
          <p className="text-xs text-slate-500">You can also do this at check-in.</p>
        </section>
      )}

      {booked && !bringLater && !allDone ? (
        <button
          type="button"
          onClick={() => setBringLater(true)}
          className="mt-6 w-full rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          I'll bring them to my visit
        </button>
      ) : null}
    </main>
  );
}
